import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { toOperationOutcome } from '~/hooks/structure-set/operationOutcome';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import { formatMm3AsCm3 } from '~/utils/units';
import ModeToggle from '~/components/toolbar/OperationsToolbar/ModeToggle';
import OperationApplyButton from '~/components/toolbar/OperationsToolbar/OperationApplyButton';
import ShowPreviewToggle from '~/components/toolbar/OperationsToolbar/ShowPreviewToggle';
import { AUTO_PREVIEW_DELAY_MS } from '~/components/toolbar/OperationsToolbar/autoPreview';
import PreviewResultStatus from '~/components/toolbar/OperationsToolbar/PreviewResultStatus';
import { usePostProcessingPreview } from '~/hooks/structure-set/usePostProcessingPreview';
import { useRoiSdfOperation } from '~/hooks/structure-set/useRoiSdfOperation';

/**
 * Per-slice, not whole-volume, on open. Every mode toggle in this panel
 * starts 2D for one reason: 2D is the lower-blast-radius direction. A 3D
 * pass reaches through slices the user has not looked at — a 3D
 * keep-largest can drop an island fifteen slices away — and an effect that
 * cannot be checked is the hazard, not the wrong answer. The rectangle
 * erase already defaults this way (SR-WOR-H-24-1).
 *
 * Deliberately a per-tab constant rather than one shared across the panel:
 * the tabs' modes are independent settings and one flipping is not a
 * reason for the others to.
 */
const DEFAULT_POST_PROCESSING_MODE: PostProcessingMode = '2D';

// ── Fill holes tab content ───────────────────────────────────────────

export default function FillTab() {
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        clearPreview,
        invalidatePreview,
        sdfRevision,
    } = usePostProcessingPreview();
    const [mode, setMode] = useState<PostProcessingMode>(
        DEFAULT_POST_PROCESSING_MODE
    );
    // True once a preview has run and found no enclosed cavity to fill.
    // Load-bearing for THIS tab above all others: a filled structure looks
    // almost exactly like the structure it was filled from, so "the result
    // is identical" and "no preview drew" are the same picture.
    const [previewFoundNothing, setPreviewFoundNothing] = useState(false);

    // The claim "this is the result" is dropped by the effect below
    // before this ever runs, so it cannot outlive the overlay it
    // describes.
    const runPreview = useCallback(() => {
        setPreviewFoundNothing(false);
        preview(
            async (srcTexture, signal) => {
                const result = await SDFOperations.applyFillCavities(
                    srcTexture,
                    mode,
                    signal
                );
                // The engine's own count, not a guess from the picture: a fill
                // that finds nothing publishes a result identical to the ROI.
                //
                // Gated on the signal, for the reason SmoothTab gates its
                // volumes: a superseded run's overlay is discarded by the
                // hook, but a late setState from it is not — it would leave
                // the OLDER run's "nothing to fill" standing beside the NEWER
                // run's picture. Under a result preview that line is the only
                // thing separating "there was no cavity" from "the preview
                // never drew", so it must describe the run on screen.
                if (!signal.aborted) {
                    setPreviewFoundNothing(result?.status === 'unchanged');
                }
                // Handed on whole: the preview reads the tag itself —
                // 'unchanged' drops any stale overlay and unlocks Apply
                // rather than being mistaken for a compute that failed.
                return result ?? { status: 'failed' };
            },
            `fill:${mode}`,
            { hidePreviewedStructure: true }
        );
    }, [preview, mode]);

    // The preview computes itself on every input that moves the operation
    // out from under the displayed result: the mode (a parameter) and
    // sdfRevision (the ROI was edited under us). Debounced, so toggling
    // 2D/3D twice does not queue two GPU computes. Skipped during an apply
    // so the commit is not disturbed.
    useEffect(() => {
        if (isProcessing) {
            return;
        }
        // invalidatePreview, not clearPreview: readiness drops SYNCHRONOUSLY
        // here, so Apply cannot commit the previous mode's result during
        // the debounce, while the picture stays up until the recompute
        // swaps it in rather than flickering to nothing.
        invalidatePreview();
        setPreviewFoundNothing(false);
        const timeout = setTimeout(() => runPreview(), AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [mode, isProcessing, sdfRevision, runPreview, invalidatePreview]);

    const handleApply = () => {
        clearPreview();
        setPreviewFoundNothing(false);
        executeOperation({
            operationFn: async (roi) => {
                const result = await SDFOperations.applyFillCavities(
                    roi.texture!,
                    mode
                );
                if (!result) {
                    return { status: 'failed' };
                }
                // Voxels are an engine unit and never reach the user: the
                // count is converted here, at the UI boundary, through the
                // spacings of the very grid the fill counted on — the same
                // conversion the Crop tab states its removal in. A filled
                // structure looks almost exactly like the one it was
                // filled from, so the toast has to say how much it gained.
                const grid = roi.texture!.textureGrid;
                const voxelVolumeMm3 = grid.resX * grid.resY * grid.resZ;
                // The engine's own tag decides whether this was an edit at
                // all: a structure with no enclosed hole comes back
                // 'unchanged', and banking an undo entry for it would cost
                // the clinician a press to return to the structure they
                // already had. Either way the toast reports the outcome —
                // 0 → "no cavities found", otherwise the volume closed.
                return toOperationOutcome(result, {
                    filled: result.filledVoxelCount,
                    volume: formatMm3AsCm3(
                        result.filledVoxelCount * voxelVolumeMm3
                    ),
                });
            },
            operationName: 'Fill Cavities',
            busyMessageId: 'postProcessing.fill.busy',
            successMessageId: 'postProcessing.fill.success',
            failedMessageId: 'postProcessing.fill.failed',
            logLabel: 'Fill cavities',
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <ModeToggle
                mode={mode}
                onChange={setMode}
                disabled={isProcessing}
            />
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="postProcessing.fill.description" />
            </Typography>
            <ShowPreviewToggle
                visible={isPreviewVisible}
                onChange={setPreviewVisible}
                disabled={!isPreviewReady}
            />
            <OperationApplyButton
                onApply={handleApply}
                isPreviewing={isPreviewing}
                isPreviewReady={isPreviewReady}
                isProcessing={isProcessing}
            />
            <PreviewResultStatus
                isPreviewOnScreen={isPreviewReady}
                previewChangesNothing={previewFoundNothing}
            />
        </Box>
    );
}
